// components/settings/surfox-settings/sections/TeamSection.jsx
// Team Management - Invites, roster, stats
// ALL existing logic preserved, UI redesigned

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Shield, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Send,
  Copy,
  Search,
  RefreshCw,
  Link,
  X,
  Phone,
  Loader2,
  UserCheck,
  UserX,
  MoreVertical
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { PERMISSIONS } from '../../../../lib/permissions';
import supabase from '../../../../lib/supabaseClient';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'development') return 'http://localhost:3001';
  return 'https://api.getsurfox.com/api';
};

const getAuthToken = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) return null;
    return session?.access_token || null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

// CSRF token cache
let csrfToken = null;
let csrfTokenExpiry = null;

const getCsrfToken = async (forceRefresh = false) => {
  // Return cached token if still valid (with 5 min buffer)
  if (!forceRefresh && csrfToken && csrfTokenExpiry && Date.now() < csrfTokenExpiry) {
    return csrfToken;
  }
  if (forceRefresh) { csrfToken = null; csrfTokenExpiry = null; }

  try {
    const token = await getAuthToken();
    const apiBaseUrl = getApiBaseUrl();
    const csrfUrl = apiBaseUrl.includes('/api')
      ? `${apiBaseUrl}/csrf-token`
      : `${apiBaseUrl}/api/csrf-token`;

    const response = await fetch(csrfUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.error('Failed to fetch CSRF token');
      return null;
    }

    const data = await response.json();
    csrfToken = data.csrfToken;
    // Set expiry to 5 minutes before actual expiry for safety
    csrfTokenExpiry = Date.now() + ((data.expiresIn - 300) * 1000);

    return csrfToken;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    return null;
  }
};

const getAuthHeaders = async (method = 'GET', forceRefresh = false) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Add CSRF token for state-changing methods
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
    const csrf = await getCsrfToken(forceRefresh);
    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
    }
  }

  return headers;
};

const makeApiCall = async (endpoint, options = {}) => {
  const method = options.method || 'GET';
  const apiBaseUrl = getApiBaseUrl();
  const fullUrl = `${apiBaseUrl}${endpoint}`;

  const doFetch = async (forceRefresh = false) => {
    const headers = await getAuthHeaders(method, forceRefresh);
    return fetch(fullUrl, { ...options, headers: { ...headers, ...options.headers } });
  };

  let response = await doFetch(false);

  // Retry once with a fresh CSRF token on stale-token 403s
  if (response.status === 403) {
    let body;
    try { body = await response.clone().json(); } catch (_) {}
    if (body?.error === 'Invalid CSRF token' || body?.error === 'CSRF token missing') {
      response = await doFetch(true);
    }
  }

  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error(`API endpoint not found.`);
    }

    const errorText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch (e) {
      errorData = { error: `Server error (${response.status})` };
    }
    throw new Error(errorData.error || `Request failed (${response.status})`);
  }

  const responseText = await response.text();
  if (!responseText) return {};

  try {
    return JSON.parse(responseText);
  } catch (parseError) {
    throw new Error(`Invalid JSON response from API`);
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid';
    
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1}d ago`;
    return date.toLocaleDateString();
  } catch (error) {
    return 'Invalid';
  }
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '—';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const getRoleDisplay = (role) => {
  switch (role) {
    case 'business_admin': return 'Admin';
    case 'user': return 'Team Member';
    case 'global_admin': return 'Global Admin';
    case 'partner_admin': return 'Partner Admin';
    case 'co_admin': return 'Co-Admin';
    default: return role || 'Team Member';
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TeamSection({ onUpdate }) {
  const { user, loading: authLoading, hasPermission } = useAuth();
  const isPartnerAdmin = user?.role === 'partner_admin' || user?.role === 'co_admin';

  // State
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [invitePhoneNumber, setInvitePhoneNumber] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [partnerLocations, setPartnerLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invitationLink, setInvitationLink] = useState(null);
  
  const [teamStats, setTeamStats] = useState({
    totalMembers: 0,
    activeUsers: 0,
    pendingInvites: 0,
    lastWeekLogins: 0
  });

  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);

  // Permission checks
  const canInviteUsers = hasPermission(PERMISSIONS.INVITE_REMOVE_USERS);
  const canManageUsers = hasPermission(PERMISSIONS.MANAGE_USERS);
  const canViewTeam = hasPermission(PERMISSIONS.VIEW_TEAM_MEMBERS);

  // Load partner locations
  const loadPartnerLocations = async () => {
    if (!isPartnerAdmin) return;
    
    setLoadingLocations(true);
    try {
      const locations = await makeApiCall('/team/partner-locations');
      setPartnerLocations(locations);
    } catch (error) {
      console.error('Failed to load partner locations:', error);
      setPartnerLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Load team data
  const loadTeamData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [statsRes, membersRes] = await Promise.allSettled([
        makeApiCall('/team/stats'),
        makeApiCall('/team/members')
      ]);

      if (statsRes.status === 'fulfilled') {
        setTeamStats(statsRes.value);
      } else {
        setTeamStats({ totalMembers: 0, activeUsers: 0, pendingInvites: 0, lastWeekLogins: 0 });
      }

      if (membersRes.status === 'fulfilled') {
        setTeamMembers(membersRes.value);
      } else {
        setTeamMembers([]);
      }

      try {
        const invites = await makeApiCall('/team/invitations');
        setPendingInvites(invites);
      } catch (inviteError) {
        setPendingInvites([]);
      }

    } catch (error) {
      console.error('Error loading team data:', error);
      setError(`Failed to load team data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (canViewTeam && !authLoading) {
      loadTeamData();
    }
  }, [canViewTeam, authLoading]);

  useEffect(() => {
    if (showInviteModal && isPartnerAdmin) {
      loadPartnerLocations();
    }
  }, [showInviteModal, isPartnerAdmin]);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Handler functions
  const handleSendInvite = async () => {
    if (!canInviteUsers) {
      setError("You don't have permission to invite users.");
      return;
    }

    if (!inviteEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!inviteFirstName.trim() || !inviteLastName.trim()) {
      setError('Please enter first and last name');
      return;
    }

    if (isPartnerAdmin && inviteRole === 'user' && !selectedLocation) {
      setError('Please select a location for this user');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (invitePhoneNumber.trim()) {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(invitePhoneNumber.replace(/[\s\-\(\)]/g, ''))) {
        setError('Please enter a valid phone number');
        return;
      }
    }
    
    setInviteLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const data = await makeApiCall('/team/invite', {
        method: 'POST',
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          firstName: inviteFirstName,
          lastName: inviteLastName,
          phoneNumber: invitePhoneNumber.trim() || null,
          ...(isPartnerAdmin && inviteRole === 'user' && selectedLocation && {
            targetTenantId: selectedLocation
          })
        })
      });
      
      setSuccess('Invitation created successfully!');
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInvitePhoneNumber('');
      setInviteRole('user');
      setSelectedLocation('');
      setShowInviteModal(false);
      
      setInvitationLink({
        email: inviteEmail,
        name: `${inviteFirstName} ${inviteLastName}`,
        url: data.signupUrl
      });

      // Team data reloads when the invitation link modal is closed
      
    } catch (error) {
      console.error('Error sending invite:', error);
      setError(error.message || 'Failed to send invitation.');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    if (!canManageUsers) {
      setError("You don't have permission to manage user status.");
      return;
    }

    try {
      const data = await makeApiCall(`/team/members/${userId}/toggle-status`, {
        method: 'PATCH'
      });
      
      setSuccess(data.message || 'User status updated!');
      await loadTeamData();
      onUpdate?.();
      
    } catch (error) {
      console.error('Error updating user status:', error);
      setError(error.message || 'Failed to update user status.');
    }
  };

  const handleResendInvite = async (invitationId) => {
    if (!canInviteUsers) {
      setError("You don't have permission to resend invitations.");
      return;
    }

    try {
      await makeApiCall(`/invitations/${invitationId}/resend`, { method: 'POST' });
      setSuccess('Invitation resent!');
      await loadTeamData();
    } catch (error) {
      console.error('Error resending invite:', error);
      setError(error.message || 'Failed to resend invitation.');
    }
  };

  const handleCancelInvite = async (invitationId) => {
    if (!canInviteUsers) {
      setError("You don't have permission to cancel invitations.");
      return;
    }

    try {
      await makeApiCall(`/team/invitations/${invitationId}`, { method: 'DELETE' });
      setSuccess('Invitation canceled!');
      await loadTeamData();
    } catch (error) {
      console.error('Error canceling invite:', error);
      setError(error.message || 'Failed to cancel invitation.');
    }
  };

  const closeInvitationLink = () => {
    setInvitationLink(null);
    loadTeamData();
    onUpdate?.();
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      inactive: 'bg-slate-50 text-slate-600 border-slate-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200'
    };
    const icons = {
      active: CheckCircle,
      inactive: XCircle,
      pending: Clock
    };
    const Icon = icons[status] || Clock;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border ${styles[status] || styles.pending}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = (member.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (member.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || member.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Permission check
  if (!canViewTeam && !authLoading) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">Access Restricted</p>
        <p className="text-sm text-slate-400 mt-1">You don't have permission to view team management.</p>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <span className="text-sm text-emerald-700 font-medium">{success}</span>
        </div>
      )}

      {/* Permission Warning */}
      {!canInviteUsers && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <span className="text-sm text-amber-700">
            Read-only access. Admin permissions required to invite or manage users.
          </span>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{teamStats.totalMembers}</p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Active</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{teamStats.activeUsers}</p>
            </div>
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Pending</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{teamStats.pendingInvites}</p>
            </div>
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Weekly Logins</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{teamStats.lastWeekLogins}</p>
            </div>
            <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-violet-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search team..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm
                         focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2.5 border border-slate-200 rounded-xl text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
          <button
            onClick={loadTeamData}
            className="p-2.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        
        {canInviteUsers && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center space-x-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl
                       text-sm font-medium hover:bg-slate-800 transition-colors shadow-sm"
          >
            <UserPlus className="w-4 h-4" />
            <span>Invite Team Member</span>
          </button>
        )}
      </div>

      {/* Team Roster */}
      {filteredMembers.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">
            {teamMembers.length === 0 ? 'No team members yet' : 'No matching members'}
          </p>
          {teamMembers.length === 0 && canInviteUsers && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="mt-3 text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              + Invite your first team member
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredMembers.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="group flex items-center justify-between p-4 bg-white hover:bg-slate-50 
                         rounded-xl border border-slate-200 hover:border-slate-300 transition-all"
            >
              <div className="flex items-center space-x-4">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                  <span className="text-sm font-semibold text-slate-600">
                    {(member.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()}
                  </span>
                </div>
                
                {/* Info */}
                <div>
                  <div className="font-medium text-slate-900">{member.name || 'Unknown'}</div>
                  <div className="flex items-center space-x-3 text-sm text-slate-500 mt-0.5">
                    <span>{member.email || 'No email'}</span>
                    <span className="text-slate-300">·</span>
                    <span>{formatPhoneNumber(member.phone_number)}</span>
                  </div>
                </div>
              </div>
              
              {/* Right side */}
              <div className="flex items-center space-x-4">
                <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg">
                  {getRoleDisplay(member.role)}
                </span>
                {getStatusBadge(member.status)}
                <span className="text-xs text-slate-400 hidden lg:inline">
                  {formatDate(member.lastLogin)}
                </span>
                
                {/* Actions */}
                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {member.status === 'pending' && canInviteUsers ? (
                    <button
                      onClick={() => handleResendInvite(member.invitationId || member.id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Resend invite"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  ) : canManageUsers && (
                    <button
                      onClick={() => handleToggleUserStatus(member.id, member.status)}
                      className={`p-2 rounded-lg transition-colors ${
                        member.status === 'active' 
                          ? 'text-red-600 hover:bg-red-50' 
                          : 'text-emerald-600 hover:bg-emerald-50'
                      }`}
                      title={member.status === 'active' ? 'Deactivate' : 'Activate'}
                    >
                      {member.status === 'active' ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-200">
            <h4 className="font-semibold text-slate-900">Pending Invitations</h4>
            <p className="text-xs text-slate-500 mt-0.5">Expire after 7 days</p>
          </div>
          <div className="divide-y divide-slate-100">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-sm font-medium text-slate-900">{invite.email}</div>
                    <div className="text-xs text-slate-500">
                      Invited {formatDate(invite.invitedDate)}
                    </div>
                  </div>
                </div>
                {canInviteUsers && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleResendInvite(invite.id)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => handleCancelInvite(invite.id)}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invitation Link Modal */}
      <AnimatePresence>
        {invitationLink && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={closeInvitationLink}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-lg w-full"
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Invitation Created</h3>
                <button
                  onClick={closeInvitationLink}
                  className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <p className="text-sm text-slate-600">
                  Send this link to <span className="font-medium text-slate-900">{invitationLink.name || invitationLink.email}</span>:
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <p className="text-xs text-slate-700 break-all font-mono">{invitationLink.url}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(invitationLink.url);
                      setSuccess('Link copied!');
                    }}
                    className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 
                               font-medium text-sm flex items-center justify-center"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </button>
                  <button
                    onClick={closeInvitationLink}
                    className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200
                               font-medium text-sm"
                  >
                    Close
                  </button>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> This link expires in 7 days.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && canInviteUsers && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full"
            >
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Invite Team Member</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">First Name</label>
                    <input
                      type="text"
                      placeholder="John"
                      value={inviteFirstName}
                      onChange={(e) => setInviteFirstName(e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Last Name</label>
                    <input
                      type="text"
                      placeholder="Doe"
                      value={inviteLastName}
                      onChange={(e) => setInviteLastName(e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm
                               focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={invitePhoneNumber}
                    onChange={(e) => setInvitePhoneNumber(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm
                               focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => {
                      setInviteRole(e.target.value);
                      if (e.target.value !== 'user') setSelectedLocation('');
                    }}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm bg-white
                               focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                  >
                    <option value="user">Team Member</option>
                    {isPartnerAdmin && (
                      <>
                        <option value="co_admin">Co-Admin</option>
                        <option value="business_admin">Business Admin (New Location)</option>
                      </>
                    )}
                    {!isPartnerAdmin && canInviteUsers && (
                      <option value="business_admin">Business Admin</option>
                    )}
                  </select>
                </div>

                {isPartnerAdmin && inviteRole === 'user' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Location</label>
                    {loadingLocations ? (
                      <div className="flex items-center px-3 py-2.5 border border-slate-200 rounded-xl">
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400 mr-2" />
                        <span className="text-sm text-slate-500">Loading...</span>
                      </div>
                    ) : (
                      <select
                        value={selectedLocation}
                        onChange={(e) => setSelectedLocation(e.target.value)}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm bg-white
                                   focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      >
                        <option value="">Select location...</option>
                        {partnerLocations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
              <div className="px-6 py-4 bg-slate-50 rounded-b-2xl flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setInviteFirstName('');
                    setInviteLastName('');
                    setInvitePhoneNumber('');
                    setInviteRole('user');
                    setSelectedLocation('');
                  }}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-xl transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvite}
                  disabled={
                    !inviteEmail.trim() || 
                    !inviteFirstName.trim() || 
                    !inviteLastName.trim() || 
                    !invitePhoneNumber.trim() || 
                    inviteLoading ||
                    (isPartnerAdmin && inviteRole === 'user' && !selectedLocation)
                  }
                  className="px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed 
                             flex items-center font-medium"
                >
                  {inviteLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Link className="w-4 h-4 mr-2" />
                      Create Invite
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}